import { FormEvent, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Field, Input, Label, Paragraph, ValidationMessage } from '@digdir/designsystemet-react';
import { AuthLayout } from '../../components/AuthLayout';
import { Routes } from '../../enums/Routes';
import { useForgotPasswordMutation } from '../../hooks/mutations/useForgotPasswordMutation';
import { isEmail } from '../LoginPage/loginView';
import classes from '../LoginPage/LoginPage.module.css';

/** Send en lenke for å lage nytt passord. Svaret er det samme om kontoen finnes eller ikke. */
export const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const initialEmail = (location.state as { email?: string } | null)?.email ?? '';
  const [email, setEmail] = useState(initialEmail);
  const [error, setError] = useState(false);
  const [sent, setSent] = useState(false);
  const mutation = useForgotPasswordMutation();

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = String(new FormData(event.currentTarget).get('email') ?? '').trim();
    setEmail(value);
    if (!isEmail(value)) {
      setError(true);
      setTimeout(() => document.getElementById('forgot-email')?.focus(), 0);
      return;
    }
    setError(false);
    mutation.mutate({ email: value });
    setSent(true);
  };

  if (sent) {
    return (
      <AuthLayout title={t('auth.forgot.sentTitle')}>
        <div className={classes.form}>
          <Alert data-color="success" role="status">
            <Paragraph>{t('auth.forgot.sent', { email })}</Paragraph>
          </Alert>
          <Button asChild variant="secondary" className={classes.fullWidth}>
            <Link to={Routes.Root}>{t('auth.forgot.backToLogin')}</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.forgot.title')} lead={t('auth.forgot.lead')}>
      <form className={classes.form} onSubmit={submit} noValidate>
        <Field>
          <Label htmlFor="forgot-email">{t('auth.login.email')}</Label>
          <Input
            id="forgot-email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="username"
            autoFocus
            defaultValue={initialEmail}
            aria-invalid={error}
            aria-describedby={error ? 'forgot-email-error' : undefined}
          />
          {error && <ValidationMessage id="forgot-email-error">{t('auth.errors.email')}</ValidationMessage>}
        </Field>
        <Button type="submit" className={classes.fullWidth}>
          {t('auth.forgot.submit')}
        </Button>
        <Button asChild variant="tertiary" className={classes.center}>
          <Link to={Routes.Root}>{t('auth.forgot.back')}</Link>
        </Button>
      </form>
    </AuthLayout>
  );
};
